import { useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Star } from "lucide-react"
import { Link } from "react-router-dom"
import { useAuth } from "@/context/Auth"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { addToCart as addToCartAction } from "@/store/slices/cartSlice"
import {
  fetchBestSellers,
  selectBestSellers,
  selectBestSellersLoading,
} from "@/store/slices/productSlice"
import { ProductListSkeleton } from "./skeletons"
import { Skeleton } from "@/components/ui/skeleton"
import { formatCurrency } from "@/utils/format"

export default function BestSellers() {
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  
  // Redux selectors
  const products = useAppSelector(selectBestSellers)
  const loading = useAppSelector(selectBestSellersLoading)

  // Fetch best sellers on mount
  useEffect(() => {
    dispatch(fetchBestSellers(6)) // limit = 6
  }, [dispatch])

  const handleAddToCart = (product) => {
    const hasDiscount = product.discount > 0;
    const finalPrice = hasDiscount
      ? product.price - (product.price * product.discount) / 100
      : product.price;

    dispatch(
      addToCartAction({
        product: {
          ...product,
          productId: product.productId, // ✅ ensure normalized ID
          price: Number(finalPrice),
          image: product.image,
        },
        quantity: 1,
        user,
      })
    );
  };

  // ✅ If still loading
  if (loading) {
    return (
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="w-48 h-8 mx-auto mb-4" />
            <Skeleton className="w-96 h-5 mx-auto" />
          </div>
          <ProductListSkeleton count={6} className="md:grid-cols-3" />
        </div>
      </section>
    )
  }

  // ✅ If no products, don't render anything
  if (!products || products.length === 0) {
    return null
  }

  return (
    <section className="py-10">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-primary mb-4">Best Sellers</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our most loved products, trusted by thousands of customers worldwide
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {products.map((product) => {
            const hasDiscount = product.discount > 0;
            const discountedPrice = hasDiscount
              ? product.price - (product.price * product.discount) / 100
              : product.price;
            const savings = hasDiscount
              ? (product.price * product.discount) / 100
              : 0;

            return (
              <Card
                key={product.productId}
                className="overflow-hidden border border-gray-100 dark:border-gray-800 rounded-xl shadow-xs hover:shadow-sm transition-shadow bg-white/70 dark:bg-[#1a1a1a]/70 backdrop-blur-sm pt-0"
              >
                <CardContent className="p-0 relative">
                  <Link to={`/products/${product.productId}`}>
                    <img
                      src={product.image || "/placeholder.svg"}
                      alt={product.name}
                      width={200}
                      height={200}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = "/placeholder.svg";
                      }}
                    />
                    {hasDiscount && (
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-xs sm:text-sm font-bold px-2 py-1 rounded-md shadow-md">
                        -{product.discount}%
                      </span>
                    )}
                  </Link>
                  <div className="p-4">

                    {/* Ratings */}
                <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating || 0)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  <span className="text-sm text-gray-500 ml-2">
                    ({product.reviews || 0})
                  </span>
                </div>

                <h3 className="font-semibold text-lg text-gray-800 mb-1">
                  <Link to={`/products/${product.productId}`}>
                    {product.name}
                  </Link>
                </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>

                    {/* ✅ Discount + Savings */}
                    <div className="flex items-center justify-between">
                      {hasDiscount ? (
                        <div className="flex flex-col">
                          <span className="text-sm text-gray-500 line-through">
                            {formatCurrency(product.price)}
                          </span>
                          <span className="text-lg sm:text-xl font-bold text-red-600">
                            {formatCurrency(discountedPrice)}
                          </span>
                          <span className="text-xs text-green-600 font-medium">
                            You save {formatCurrency(savings)} ({product.discount}%)
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg sm:text-xl font-bold text-gray-900">
                          {formatCurrency(product.price)}
                        </span>
                      )}
                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="bg-primary hover:bg-primary/90 text-white"
                        disabled={product.stock_quantity <= 0}
                      >
                        {product.stock_quantity <= 0 ? "Out of Stock" : "Add to Cart"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  )
}
